import moment from 'moment';

// Colours — deep greens from our Operations palette + the User Access Control
// accent set (no light green).
export const TB_COLORS = {
  green: '#067647',
  orange: '#f79009',
  red: '#f04438',
  slate: '#64748b',
  grey: '#98a2b3',
  lightGrey: '#d0d5dd',
  blue: '#1d4ed8',
  text: '#101828',
  subtext: '#475467',
  border: '#eaecf0',
};

// Task statuses stored on the wire -> display. (Reuse-only: the model has
// todo/underway/completed; there is no "Blocked" status.)
export const STATUS_META = {
  todo: { key: 'todo', label: 'To Do', accent: TB_COLORS.slate, pillBg: '#f2f4f7', pillText: TB_COLORS.subtext },
  underway: { key: 'underway', label: 'In Progress', accent: TB_COLORS.orange, pillBg: '#fff4ed', pillText: '#b93815' },
  completed: { key: 'completed', label: 'Completed', accent: TB_COLORS.green, pillBg: '#ecfdf3', pillText: TB_COLORS.green },
};

// Board column order.
export const STATUS_ORDER = ['todo', 'underway', 'completed'];

export const statusMeta = (status) =>
  STATUS_META[status] || { key: status, label: status || '—', accent: TB_COLORS.grey, pillBg: '#f2f4f7', pillText: TB_COLORS.subtext };

// Priorities stored on the wire are high/medium/low; critical/urgent are
// handled defensively in case the data ever contains them.
export const PRIORITY_META = {
  critical: { label: 'Critical', color: TB_COLORS.red },
  urgent: { label: 'Critical', color: TB_COLORS.red },
  high: { label: 'High', color: TB_COLORS.orange },
  medium: { label: 'Medium', color: TB_COLORS.grey },
  low: { label: 'Low', color: TB_COLORS.lightGrey },
};

export const priorityMeta = (priority) =>
  PRIORITY_META[(priority || '').toLowerCase()] || PRIORITY_META.medium;

// Tags are a single pipe-delimited string on the task.
export const parseTags = (tagStr) =>
  String(tagStr || '')
    .split('|')
    .map((t) => t.trim())
    .filter(Boolean);

// Soft, deterministic colour per tag name.
const TAG_PALETTE = [
  { bg: '#f2f4f7', color: '#475467' },
  { bg: '#fef3c7', color: '#92400e' },
  { bg: '#dbeafe', color: '#1d4ed8' },
  { bg: '#ecfdf3', color: '#067647' },
  { bg: '#f4f3ff', color: '#5925dc' },
  { bg: '#fff4ed', color: '#b93815' },
  { bg: '#fce7f6', color: '#c11574' },
  { bg: '#f0fdfa', color: '#0f766e' },
];
export const tagColor = (tag) => {
  let h = 0;
  for (let i = 0; i < tag.length; i += 1) h = (h * 31 + tag.charCodeAt(i)) & 0xffffff;
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
};

export const initials = (name) =>
  (String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()) || '?';

// Due-date display: "Today" / "12 Feb", and overdue when past and not completed.
export const dueMeta = (dueDate, status) => {
  if (!dueDate) return null;
  const d = moment(dueDate, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601]);
  if (!d.isValid()) return null;
  const today = moment().startOf('day');
  const isToday = d.clone().startOf('day').isSame(today);
  const overdue = d.clone().startOf('day').isBefore(today) && status !== 'completed';
  return {
    label: isToday ? 'Today' : d.format('D MMM'),
    date: d,
    isToday,
    overdue,
  };
};

// A stable colour for an assignee avatar.
export const avatarColor = (seed) => {
  const palette = ['#0B2A1B', '#067647', '#1d4ed8', '#b93815', '#5925dc', '#0f766e', '#c11574'];
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) & 0xffffff;
  return palette[Math.abs(h) % palette.length];
};

// Apply the filter bar's selections to a task list.
export const filterTasks = (tasks, filters, areaMap) => {
  const { assignee, priority, status, tag, dateRange, search } = filters;
  const q = (search || '').trim().toLowerCase();
  return (tasks || []).filter((t) => {
    if (assignee && String(t.assignee?.id ?? '') !== String(assignee)) return false;
    if (priority && (t.priority || '').toLowerCase() !== priority) return false;
    if (status && t.status !== status) return false;
    if (tag && !parseTags(t.tags).includes(tag)) return false;
    if (dateRange && dateRange.length === 2 && t.due_date) {
      const d = moment(t.due_date);
      if (d.isValid() && (d.isBefore(dateRange[0], 'day') || d.isAfter(dateRange[1], 'day'))) return false;
    }
    if (q) {
      const area = areaMap?.[t.primary_object_id] || '';
      const hay = `${t.title || ''} ${t.details || ''} ${area} ${parseTags(t.tags).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};
